<template>
  <HLSpace vertical :size="24">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Test Runs</h1>
        <p class="text-gray-600">Manage and execute test runs</p>
      </div>
      <div class="flex items-center space-x-4">
        <Button
          @click="refreshData"
          variant="outline"
          :loading="loading"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </Button>
        <Button
          @click="showCreateModal = true"
          :disabled="!canCreate"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Test Run
        </Button>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow p-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <Select
            v-model="filters.projectId"
            :options="projectOptions"
            placeholder="All Projects"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <Select
            v-model="filters.status"
            :options="statusOptions"
            placeholder="All Statuses"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
          <Select
            v-model="filters.assigneeId"
            :options="assigneeOptions"
            placeholder="All Assignees"
            @change="applyFilters"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <Input
            v-model="filters.search"
            placeholder="Search test runs..."
            @input="applyFilters"
          />
        </div>
      </div>
    </div>

    <!-- Test Runs List -->
    <div class="bg-white rounded-lg shadow">
      <div v-if="loading" class="p-8 flex flex-col items-center justify-center">
        <HLSpin id="testruns-list-loading-spinner" size="lg" :show="loading">
          <template #description>
            <HLText variant="body-sm" color="muted">Loading test runs...</HLText>
          </template>
        </HLSpin>
      </div>
      <div v-else-if="testRuns.length === 0" class="p-8 text-center">
        <EmptyState
          title="No test runs found"
          description="Create your first test run to get started."
        />
      </div>
      <div v-else class="overflow-hidden">
        <Table
          :headers="tableHeaders"
          :data="testRuns"
          :loading="loading"
          @row-click="viewTestRun"
        >
          <template #cell-name="{ row }">
            <div class="flex items-center space-x-3">
              <div class="flex-shrink-0">
                <div class="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                  <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ row.name }}</p>
                <p class="text-sm text-gray-500">{{ row.projectName }}</p>
              </div>
            </div>
          </template>
          
          <template #cell-status="{ row }">
            <HLBadge :id="`testrun-status-${row.id}`" :color="getStatusVariant(row.status)" size="sm">
              {{ row.status }}
            </HLBadge>
          </template>
          
          <template #cell-progress="{ row }">
            <div class="flex items-center space-x-2">
              <div class="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  :style="{ width: `${row.progress}%` }"
                ></div>
              </div>
              <span class="text-sm text-gray-600">{{ row.progress }}%</span>
            </div>
          </template>
          
          <template #cell-assignee="{ row }">
            <div class="flex items-center space-x-2">
              <div class="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                <svg class="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span class="text-sm text-gray-900">{{ row.assigneeName || 'Unassigned' }}</span>
            </div>
          </template>
          
          <template #cell-eta="{ row }">
            <div class="text-sm text-gray-900">
              {{ row.eta ? formatDate(row.eta) : 'Not set' }}
            </div>
          </template>
          
          <template #cell-dates="{ row }">
            <div class="text-sm">
              <p class="text-gray-900">Started: {{ formatDate(row.startDate) }}</p>
              <p class="text-gray-500">Due: {{ formatDate(row.endDate) }}</p>
            </div>
          </template>
          
          <template #cell-actions="{ row }">
            <div class="flex items-center space-x-2">
              <Button
                @click.stop="viewTestRun(row)"
                variant="outline"
                size="sm"
              >
                View
              </Button>
              <Button
                v-if="canEdit(row)"
                @click.stop="editTestRun(row)"
                variant="outline"
                size="sm"
              >
                Edit
              </Button>
              <Button
                v-if="canDelete(row)"
                @click.stop="deleteTestRun(row)"
                variant="outline"
                size="sm"
                class="text-red-600 hover:text-red-700"
              >
                Delete
              </Button>
            </div>
          </template>
        </Table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-between">
      <div class="text-sm text-gray-700">
        Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalItems) }} of {{ totalItems }} results
      </div>
      <div class="flex items-center space-x-2">
        <Button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          variant="outline"
          size="sm"
        >
          Previous
        </Button>
        <span class="text-sm text-gray-700">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <Button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          variant="outline"
          size="sm"
        >
          Next
        </Button>
      </div>
    </div>

    <!-- Create Test Run Modal -->
    <Modal
      v-model:open="showCreateModal"
      title="Create Test Run"
      size="lg"
    >
      <form @submit.prevent="createTestRun" class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <Input
              v-model="newTestRun.name"
              placeholder="Enter test run name"
              required
              :disabled="creating"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Project *
            </label>
            <Select
              v-model="newTestRun.projectId"
              :options="projectOptions"
              placeholder="Select project"
              required
              :disabled="creating"
            />
          </div>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            v-model="newTestRun.description"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter test run description"
            :disabled="creating"
          ></textarea>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Start Date *
            </label>
            <Input
              v-model="newTestRun.startDate"
              type="date"
              required
              :disabled="creating"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              End Date *
            </label>
            <Input
              v-model="newTestRun.endDate"
              type="date"
              required
              :disabled="creating"
            />
          </div>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Assignee
          </label>
          <Select
            v-model="newTestRun.assigneeId"
            :options="assigneeOptions"
            placeholder="Select assignee"
            :disabled="creating"
          />
        </div>
        
        <div class="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            @click="showCreateModal = false"
            :disabled="creating"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            :loading="creating"
            :disabled="!newTestRun.name || !newTestRun.projectId || !newTestRun.startDate || !newTestRun.endDate"
          >
            Create Test Run
          </Button>
        </div>
      </form>
    </Modal>
  </HLSpace>
</template>

<script setup>
import { ref, computed, onMounted, onActivated, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTestRunStore } from '@/stores/testRun'
import { useProjectStore } from '@/stores/project'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'
import { TEST_RUN_STATUS, TEST_RUN_STATUS_OPTIONS } from '@/constants/testRunConstants'
import { HLButton, HLBadge, HLSpace, HLSpin, HLText } from '@platform-ui/highrise'
import Table from '@/components/ui/Table.vue'
import Modal from '@/components/ui/Modal.vue'
import Input from '@/components/ui/Input.vue'
import Select from '@/components/ui/Select.vue'
import EmptyState from '@/components/ui/EmptyState.vue'

const router = useRouter()
const testRunStore = useTestRunStore()
const projectStore = useProjectStore()
const userStore = useUserStore()
const authStore = useAuthStore()
const { showToast } = useToast()

const loading = ref(false)
const testRuns = ref([])
const projects = ref([])
const users = ref([])
const showCreateModal = ref(false)
const creating = ref(false)

const filters = ref({
  projectId: '',
  status: '',
  assigneeId: '',
  search: ''
})

const pagination = ref({
  page: 1,
  pageSize: 10,
  total: 0
})

const newTestRun = ref({
  name: '',
  description: '',
  projectId: '',
  startDate: '',
  endDate: '',
  assigneeId: ''
})

const tableHeaders = [
  { key: 'name', label: 'Name' },
  { key: 'status', label: 'Status' },
  { key: 'progress', label: 'Progress' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'eta', label: 'ETA' },
  { key: 'dates', label: 'Dates' },
  { key: 'actions', label: 'Actions' }
]

const statusOptions = TEST_RUN_STATUS_OPTIONS

const projectOptions = computed(() => [
  { value: '', label: 'All Projects' },
  ...projects.value.map(project => ({
    value: project.id,
    label: project.name
  }))
])

const assigneeOptions = computed(() => [
  { value: '', label: 'All Assignees' },
  ...users.value.map(user => ({
    value: user.id,
    label: user.name
  }))
])


const currentPage = computed(() => pagination.value.page)
const pageSize = computed(() => pagination.value.pageSize)
const totalItems = computed(() => pagination.value.total)
const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value))

const canCreate = computed(() => {
  return authStore.user?.role === 'ADMIN' || authStore.user?.role === 'QA_MANAGER'
})

const getStatusVariant = (status) => {
  const variants = {
    ['YET_TO_START']: 'secondary',
    ['IN_PROGRESS']: 'warning',
    ['COMPLETED']: 'success',
    ['CANCELLED']: 'danger'
  }
  return variants[status] || 'secondary'
}

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const canEdit = (testRun) => {
  if (!authStore.user) return false
  return authStore.user.role === 'ADMIN' || 
         authStore.user.role === 'QA_MANAGER' ||
         testRun.assigneeId === authStore.user.id
}

const canDelete = (testRun) => {
  if (!authStore.user) return false
  return authStore.user.role === 'ADMIN' || 
         authStore.user.role === 'QA_MANAGER'
}

// OPTIMIZATION: SessionStorage caching
const CACHE_KEY_PREFIX = 'testruns_'
const CACHE_DURATION = 30000 // 30 seconds

const getCache = (key) => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY_PREFIX + key)
    if (cached) {
      const { data, timestamp } = JSON.parse(cached)
      const now = Date.now()
      if ((now - timestamp) < CACHE_DURATION) {
        return data
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null
}

const setCache = (key, data) => {
  try {
    sessionStorage.setItem(CACHE_KEY_PREFIX + key, JSON.stringify({
      data,
      timestamp: Date.now()
    }))
  } catch (e) {
    // Ignore cache errors (quota exceeded, etc.)
  }
}

const loadTestRuns = async () => {
  loading.value = true
  try {
    const response = await testRunStore.getTestRuns({
      ...filters.value,
      page: pagination.value.page,
      pageSize: pagination.value.pageSize
    })
    
    let loadedTestRuns = response.data || []
    
    // Sort by creation timestamp in descending order (newest first)
    loadedTestRuns.sort((a, b) => {
      const dateA = a.created_at || a.createdAt
      const dateB = b.created_at || b.createdAt
      let timeA = 0
      let timeB = 0
      
      if (dateA) {
        if (dateA instanceof Date) {
          timeA = dateA.getTime()
        } else if (typeof dateA === 'string') {
          const parsed = new Date(dateA).getTime()
          timeA = isNaN(parsed) ? 0 : parsed
        } else if (typeof dateA === 'number') {
          timeA = dateA
        }
      }
      
      if (dateB) {
        if (dateB instanceof Date) {
          timeB = dateB.getTime()
        } else if (typeof dateB === 'string') {
          const parsed = new Date(dateB).getTime()
          timeB = isNaN(parsed) ? 0 : parsed
        } else if (typeof dateB === 'number') {
          timeB = dateB
        }
      }
      
      return timeB - timeA // Descending order (newest first)
    })
    
    testRuns.value = loadedTestRuns
    pagination.value.total = response.total || 0
    
    // Cache the results
    setCache('testRuns', { data: testRuns.value, total: pagination.value.total, filters: filters.value, page: pagination.value.page })
  } catch (error) {
    console.error('Error loading test runs:', error)
    showToast('Error loading test runs', 'error')
    testRuns.value = []
  } finally {
    loading.value = false
  }
}

const loadProjects = async () => {
  try {
    // Check cache first
    const cached = getCache('projects')
    if (cached) {
      projects.value = cached
      return
    }
    
    const response = await projectStore.getProjects()
    projects.value = response || []
    
    // Cache the results
    setCache('projects', projects.value)
  } catch (error) {
    console.error('Error loading projects:', error)
  }
}

const loadUsers = async () => {
  try {
    // Check cache first
    const cached = getCache('users')
    if (cached) {
      users.value = cached
      return
    }
    
    const response = await userStore.getUsers()
    users.value = response || []
    
    // Cache the results
    setCache('users', users.value)
  } catch (error) {
    console.error('Error loading users:', error)
  }
}

const applyFilters = () => {
  pagination.value.page = 1
  loadTestRuns()
}

const refreshData = () => {
  loadTestRuns()
}

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    pagination.value.page = page
    loadTestRuns()
  }
}

const viewTestRun = (testRun) => {
  // Use project-scoped route if projectId is available
  if (testRun.project_id) {
    router.push(`/projects/${testRun.project_id}/test-runs/${testRun.id}`)
  } else {
    // Fallback to direct route if no project context
    router.push(`/test-runs/${testRun.id}`)
  }
}

const editTestRun = (testRun) => {
  // Use project-scoped route if projectId is available
  if (testRun.project_id) {
    router.push(`/projects/${testRun.project_id}/test-runs/${testRun.id}/edit`)
  } else {
    // Fallback to direct route if no project context
    router.push(`/test-runs/${testRun.id}/edit`)
  }
}

const deleteTestRun = async (testRun) => {
  if (!confirm(`Are you sure you want to delete "${testRun.name}"?`)) {
    return
  }

  try {
    await testRunStore.deleteTestRun(testRun.id)
    showToast('Test run deleted successfully', 'success')
    loadTestRuns()
  } catch (error) {
    console.error('Error deleting test run:', error)
    showToast('Error deleting test run', 'error')
  }
}

const createTestRun = async () => {
  try {
    creating.value = true
    
    await testRunStore.createTestRun({
      ...newTestRun.value,
      status: 'YET_TO_START',
      progress: 0
    })

    showToast('Test run created successfully', 'success')
    showCreateModal.value = false
    newTestRun.value = {
      name: '',
      description: '',
      projectId: '',
      startDate: '',
      endDate: '',
      assigneeId: ''
    }
    loadTestRuns()
  } catch (error) {
    console.error('Error creating test run:', error)
    showToast('Error creating test run', 'error')
  } finally {
    creating.value = false
  }
}

// Watch for filter changes
watch(filters, () => {
  applyFilters()
}, { deep: true })

onMounted(() => {
  // OPTIMIZATION: Try to load from cache first
  const cachedTestRuns = getCache('testRuns')
  const cachedProjects = getCache('projects')
  const cachedUsers = getCache('users')
  
  if (cachedTestRuns && cachedProjects && cachedUsers) {
    // Check if filters match cached filters
    const filtersMatch = JSON.stringify(cachedTestRuns.filters) === JSON.stringify(filters.value) &&
                         cachedTestRuns.page === pagination.value.page
    
    if (filtersMatch) {
      testRuns.value = cachedTestRuns.data || []
      pagination.value.total = cachedTestRuns.total || 0
      projects.value = cachedProjects
      users.value = cachedUsers
      loading.value = false
      return
    }
  }
  
  // Load fresh data (will cache after loading)
  loadProjects()
  loadUsers()
  loadTestRuns()
})

// OPTIMIZATION: Check cache when component is activated (tab switching)
onActivated(() => {
  const cachedTestRuns = getCache('testRuns')
  const cachedProjects = getCache('projects')
  const cachedUsers = getCache('users')
  
  if (cachedTestRuns && cachedProjects && cachedUsers) {
    const filtersMatch = JSON.stringify(cachedTestRuns.filters) === JSON.stringify(filters.value) &&
                         cachedTestRuns.page === pagination.value.page
    
    if (filtersMatch && testRuns.value.length === 0) {
      testRuns.value = cachedTestRuns.data || []
      pagination.value.total = cachedTestRuns.total || 0
      projects.value = cachedProjects
      users.value = cachedUsers
      loading.value = false
      return
    }
  }
  
  // Only reload if data is missing
  if (testRuns.value.length === 0) loadTestRuns()
  if (projects.value.length === 0) loadProjects()
  if (users.value.length === 0) loadUsers()
})
</script>